'use client'

import { Fragment, useEffect, useState } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { useParams } from 'next/navigation'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useHsTaxListByHsCode } from '@/modules/hs/hooks/useHsTax'
import type {
  HsTaxCommonResponse,
  HsTaxListResponse,
} from '@/modules/hs/types/hs-tax.response'
import NKUDDB_FORM from '@/modules/hs/constants/nkuddb-form.json'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
)

const months = [
  'Tháng 1',
  'Tháng 2',
  'Tháng 3',
  'Tháng 4',
  'Tháng 5',
  'Tháng 6',
  'Tháng 7',
  'Tháng 8',
  'Tháng 9',
  'Tháng 10',
  'Tháng 11',
  'Tháng 12',
]

const colors = [
  '#2ca02c', // Green
  '#d62728', // Red
  '#9467bd', // Purple
  '#8c564b', // Brown
  '#e377c2', // Pink
  '#7f7f7f', // Gray
  '#bcbd22', // Olive
  '#17becf', // Cyan
]

const processDataForChart = (
  records: HsTaxCommonResponse[],
  selectedYear: number,
) => {
  const data = months.map((_, monthIndex) => {
    const applicableRecord = records.find((record) => {
      const effectiveFrom = new Date(record.effectiveFrom)
      const effectiveTo = new Date(record.effectiveTo)
      const targetDate = new Date(selectedYear, monthIndex, 15)

      return targetDate >= effectiveFrom && targetDate <= effectiveTo
    })

    return applicableRecord && applicableRecord.taxRate !== null && applicableRecord.taxRate >= 0
      ? applicableRecord.taxRate
      : null
  })

  return {
    labels: months,
    data,
  }
}

const processNKUDDBForChart = (
  taxes: Record<string, HsTaxCommonResponse[]>,
  selectedYear: number,
) => {
  const finalTaxes = []
  const taxIds = Object.keys(taxes).sort((a, b) => {
    const aIndex = NKUDDB_FORM.findIndex((item) => item.key === a)
    const bIndex = NKUDDB_FORM.findIndex((item) => item.key === b)
    return aIndex - bIndex
  })

  let index = 0
  for (const taxId of taxIds) {
    const records = taxes[taxId]
    const data = months.map((_, monthIndex) => {
      const applicableRecord = records.find((record) => {
        const effectiveFrom = new Date(record.effectiveFrom)
        const effectiveTo = new Date(record.effectiveTo)
        const targetDate = new Date(selectedYear, monthIndex, 15)
        return effectiveFrom <= targetDate && targetDate <= effectiveTo
      })
      return applicableRecord && applicableRecord.taxRate >= 0
        ? applicableRecord.taxRate
        : null
    })
    const form = NKUDDB_FORM.find((form) => form.key === taxId)

    if (!form) continue

    if (data.filter((d) => d !== null).length === 0) {
      continue
    }

    index++
    const color = colors[index % colors.length]

    const label = `${form?.tenRutgon}`

    finalTaxes.push({
      label,
      data,
      borderColor: color,
      backgroundColor: color + '20', // Add transparency to hex color
      borderWidth: 2,
      tension: 0,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: color,
      pointBorderColor: '#ffffff',
      pointBorderWidth: 1,
    })
  }

  return finalTaxes
}

const getAvailableYears = (taxData: HsTaxListResponse): number[] => {
  const years = new Set<number>()

  Object.values(taxData.nkuddb)
    .flat()
    .forEach((record) => {
      const fromYear = new Date(record.effectiveFrom).getFullYear()
      const toYear = new Date(record.effectiveTo).getFullYear()

      for (let year = fromYear; year <= toYear; year++) {
        years.add(year)
      }
    })

  return Array.from(years).sort()
}

export default function HsTaxChartComponent() {
  const { hsCode } = useParams()
  const { data: taxData, isLoading } = useHsTaxListByHsCode(hsCode as string)

  const availableYears = taxData ? getAvailableYears(taxData) : []
  const [selectedYear, setSelectedYear] = useState<number>(
    availableYears.length > 0
      ? availableYears[availableYears.length - 1]
      : new Date().getFullYear(),
  )

  const [selectedTax, setSelectedTax] = useState<string | null>(null)
  const [isLegendExpanded, setIsLegendExpanded] = useState(false)

  // Update selectedYear when availableYears changes
  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[availableYears.length - 1])
    }
  }, [availableYears, selectedYear])

  if (isLoading || !taxData) {
    return (
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-[#e6f2ff] p-2 rounded">
            <svg
              className="w-6 h-6 text-[#001c5d]"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
            </svg>
          </div>
          <h2 className="text-lg font-bold">Diễn biến thuế suất</h2>
        </div>
        <div className="bg-white border border-gray-200 rounded p-4">
          <p className="text-[13px] text-[#666] text-center">
            {isLoading ? 'Đang tải...' : 'Không có dữ liệu'}
          </p>
        </div>
      </div>
    )
  }

  const nkttData = processDataForChart(taxData.nktt || [], selectedYear)
  const nkudData = processDataForChart(taxData.nkud || [], selectedYear)
  const nkuddbData = processNKUDDBForChart(taxData.nkuddb || {}, selectedYear)
  const ttdbData = processDataForChart(taxData.ttdb || [], selectedYear)
  const bvmtData = processDataForChart(taxData.bvmt || [], selectedYear)

  // Get labels from taxTable if available
  const nkttLabel =
    taxData.nktt?.[0]?.taxTable?.tenBangVn || 'Thuế nhập khẩu thông thường'
  const nkudLabel =
    taxData.nkud?.[0]?.taxTable?.tenBangVn || 'Thuế nhập khẩu ưu đãi'

  // Combine all datasets into one chart
  const datasets = []

  if (nkttData.data.some((d) => d !== null)) {
    datasets.push({
      label: nkttLabel,
      data: nkttData.data,
      borderColor: '#3b82f6', // Blue for NKTT
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      borderWidth: 2,
      tension: 0,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: '#3b82f6',
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
    })
  }

  if (nkudData.data.some((d) => d !== null)) {
    datasets.push({
      label: nkudLabel,
      data: nkudData.data,
      borderColor: '#ec4899', // Pink for NKUD
      backgroundColor: 'rgba(236, 72, 153, 0.1)',
      borderWidth: 2,
      tension: 0,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: '#ec4899',
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
    })
  }

  datasets.push(...nkuddbData)

  if (ttdbData.data.some((d) => d !== null)) {
    datasets.push({
      label: 'Thuế tiêu thụ đặc biệt',
      data: ttdbData.data,
      borderColor: '#f59e0b', // Orange for TTD
      backgroundColor: 'rgba(245, 158, 11, 0.1)',
      borderWidth: 2,
      tension: 0,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: '#f59e0b',
      pointBorderColor: '#ffffff',
      pointBorderWidth: 2,
    })
  }

  // Hide environmental protection tax (BVMT) as requested
  // if (bvmtData.data.some((d) => d !== null)) { ... }

  let combinedChartData = {
    labels: nkttData.labels,
    datasets,
  }

  const filteredChartData = {
    labels: nkttData.labels,
    datasets: combinedChartData.datasets.filter(
      (dataset) => dataset.label === selectedTax,
    ),
  }

  const renderData = selectedTax ? filteredChartData : combinedChartData

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide legend from chart
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: '#e5e7eb',
        },
        ticks: {
          font: {
            size: 12,
          },
        },
      },
      x: {
        grid: {
          display: true,
          color: '#e5e7eb',
        },
        ticks: {
          font: {
            size: 12,
          },
        },
      },
    },
  }

  // Get legend items
  const legendItems = combinedChartData.datasets.map((dataset) => ({
    label: dataset.label,
    color: dataset.borderColor as string,
  }))

  return (
    <div className="mb-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-[10px] rounded-md">
              <svg
                className="w-6 h-6 text-blue-800"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold">Diễn biến thuế suất</h2>
          </div>

          {availableYears.length > 0 && (
            <div className="flex items-center gap-0.5">
              <span className="text-sm text-gray-700 font-medium">Năm:</span>
              {availableYears.map((year, index) => (
                <Fragment key={year}>
                  <button
                    onClick={() => setSelectedYear(year)}
                    className={`text-sm px-2 py-1 bg-transparent border-none cursor-pointer transition-all duration-200 ${selectedYear === year
                      ? 'font-bold text-blue-900'
                      : 'font-normal text-gray-500'
                      }`}
                  >
                    {year}
                  </button>
                  {index < availableYears.length - 1 && (
                    <span className="text-gray-300">|</span>
                  )}
                </Fragment>
              ))}
            </div>
          )}
        </div>

        {availableYears.length > 0 ? (
          <>
            <div className="h-[400px] bg-white mb-6">
              <Line data={renderData} options={options} />
            </div>

            {/* Custom Legend */}
            <div className="grid gap-3 pt-4 border-t border-gray-200 [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]">
              {(isLegendExpanded ? legendItems : legendItems.slice(0, 10)).map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  <div
                    className="w-5 h-3 rounded-[2px] shrink-0"
                    style={{ backgroundColor: item.color }}
                  />
                  <span
                    onClick={() => {
                      if (selectedTax === item.label) {
                        setSelectedTax(null)
                      } else {
                        setSelectedTax(item.label)
                      }
                    }}
                    className={`cursor-pointer text-[13px] text-gray-700 leading-[1.4] ${selectedTax === item.label ? 'font-bold' : 'font-normal'
                      }`}
                  >
                    {item.label}
                  </span>
                </div>
              ))}
            </div>

            {legendItems.length > 10 && (
              <div className="flex justify-center mt-6">
                <button
                  onClick={() => setIsLegendExpanded(!isLegendExpanded)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {isLegendExpanded ? (
                    <>
                      Thu gọn <ChevronUp className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Xem thêm <ChevronDown className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500 text-center p-10">
            Không có dữ liệu thuế suất
          </p>
        )}
      </div>
    </div>
  )
}